import { ArrowForwardIos, CheckCircle } from '@mui/icons-material';
import { Box, Button, Divider, Grid, Typography, alpha } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { getAllGroupMessageEmailTemplates } from 'src/shared/services/groupMessageService';
import { Stack } from '@mui/system';
import { useNavigate } from 'react-router-dom';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import { GroupMessageEmailTemplate } from '../models/groupMessageEmailTemplate';
import { GroupMessageEmailTemplateView } from './groupMessageEmailTemplateView';

type EmailTemplatePickerModalProps = {
  open: boolean;
  openModal: (open: boolean) => void;
};

export const EmailTemplatePickerModal = (props: EmailTemplatePickerModalProps) => {
  const { open, openModal } = props;
  const { t } = useTranslation();
  const isMounted = useIsMounted();

  const [loading, setLoading] = useState<boolean>(false);
  const [emailTemplates, setEmailTemplates] = useState<GroupMessageEmailTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number>(0);

  const navigate = useNavigate();

  const setEmailTemplate = () => {
    const template = emailTemplates.find((et) => et.id === selectedTemplateId);
    if (!template) return;
    navigate(`${RoutesEnum.GroupMessageEmailCreateFromTemplate}/${template.id}`);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAllGroupMessageEmailTemplates();
      if (isMounted()) {
        setEmailTemplates(res.data);
      }
    } catch (e) {
      ConsoleHelper.log(e);
    } finally {
      setLoading(false);
    }
  }, [isMounted]);

  useEffect(() => {
    if (isMounted()) {
      fetchData();
    }
  }, [fetchData, isMounted]);

  return (
    <GenericDialog
      open={open}
      onClose={() => openModal(false)}
      onSave={() => ConsoleHelper.log()}
      dialogTitle={t('groupMessage:create.createEmailGroupMessage')}
      dialogContent={
        <Grid container gap={4} justifyContent='center'>
          <LoadingOverlay loading={loading} />
          <Grid item xs={12}>
            <Typography variant='body1' sx={{ fontWeight: 600, mb: -1 }}>
              {t('groupMessage:create.selectTemplate')}
            </Typography>
          </Grid>

          {emailTemplates.map((template) => (
            <Grid item xs={12} sm={6} md={3} key={template.id} sx={{ minWidth: '20rem', maxWidth: '20rem' }}>
              <Button
                variant='outlined'
                fullWidth
                sx={{
                  p: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'stretch',
                }}
                onClick={() => setSelectedTemplateId(template.id)}
              >
                <Box
                  sx={{
                    height: 'stretch',
                    flexGrow: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <GroupMessageEmailTemplateView body={template.body} />
                </Box>

                <Grid
                  className={selectedTemplateId === template.id ? 'selected' : ''}
                  item
                  xs={12}
                  sx={{
                    width: '100%',
                    height: '25rem',
                    backgroundColor: selectedTemplateId === template.id ? (theme) => alpha(theme.palette.success.light, 0.2) : 'transparent',
                  }}
                >
                  <Divider />

                  <Stack direction='row' justifyContent='center' alignItems='center' spacing={1} sx={{ mt: 1, p: 1.5 }}>
                    <Typography
                      variant='body2'
                      sx={{
                        fontWeight: 100,
                        display: 'flex',
                        alignItems: 'center',
                        textTransform: 'uppercase',
                      }}
                    >
                      {t(template.name)}
                    </Typography>
                    {selectedTemplateId === template.id && (
                      <CheckCircle
                        sx={{
                          width: '1.5rem',
                          height: '1.5rem',
                          opacity: 1,
                          ml: 1,
                          color: (thm) => thm.palette.success.main,
                        }}
                      />
                    )}
                  </Stack>
                </Grid>
              </Button>
            </Grid>
          ))}
        </Grid>
      }
      dialogActions={
        <Button
          variant='contained'
          color='primary'
          endIcon={<ArrowForwardIos fontSize='small' />}
          onClick={() => setEmailTemplate()}
          disableElevation
          disabled={!selectedTemplateId}
        >
          {t('common:button.continue')}
        </Button>
      }
    />
  );
};
